import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../../../lib/api';
import { errorText, qtyLabel } from '../../../lib/crm';
import { Button, ErrorState, Field, Input, Modal, Skeleton, Textarea, useToast } from '../../../components/ui';

/** Dispatch goods that are in stock for an order: creates a delivery challan. */
export function DispatchModal({ orderId, open, onClose, onDone }: {
  orderId: string | null; open: boolean; onClose: () => void; onDone?: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const order = useQuery({
    queryKey: ['crm', 'order', orderId], enabled: open && !!orderId,
    queryFn: () => api.get(`/crm/orders/${orderId}`),
  });
  const [qty, setQty] = useState<Record<string, string>>({});
  const [f, setF] = useState({ transporter: '', vehicleNo: '', lrNumber: '', deliveryAddress: '', dispatchDate: '' });
  const [created, setCreated] = useState<{ id: string; challanNo: string } | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => { if (open) { setQty({}); setCreated(null); setError(null); } }, [open, orderId]);

  const lines = (order.data?.items ?? []).filter((i: any) => i.kind === 'product');
  const chosen = lines
    .map((i: any) => ({ orderItemId: i.id, quantity: Number(qty[i.id] || 0) }))
    .filter((x: any) => x.quantity > 0);

  const save = useMutation({
    mutationFn: () => api.post(`/crm/orders/${orderId}/challans`, {
      items: chosen,
      transporter: f.transporter || undefined, vehicleNo: f.vehicleNo || undefined, lrNumber: f.lrNumber || undefined,
      deliveryAddress: f.deliveryAddress || undefined, dispatchDate: f.dispatchDate || undefined,
    }),
    onSuccess: (r: any) => {
      setCreated(r);
      toast.success('Goods dispatched', `Challan ${r.challanNo} created.`);
      void qc.invalidateQueries({ queryKey: ['crm'] });
      onDone?.();
    },
    onError: (e) => { setError(e); toast.error('Could not dispatch', errorText(e)); },
  });
  const fe = (k: string) => (error instanceof ApiRequestError ? error.fieldError(k) : undefined);

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Dispatch goods"
      description="Only stock already reserved for this order can leave. The rest follows when procurement delivers it."
      footer={created ? <Button onClick={onClose}>Close</Button> : (
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!chosen.length} onClick={() => save.mutate()}>
            Create challan
          </Button>
        </>
      )}>
      {created ? (
        <div className="space-y-3 text-sm">
          <p className="text-ink-800">Delivery challan <strong>{created.challanNo}</strong> has been created and stock updated.</p>
          <Link className="font-medium text-brand-600 hover:underline" to={`/crm/finance/challans/${created.id}/document`} target="_blank">
            Open printable challan
          </Link>
        </div>
      ) : order.isLoading ? <Skeleton className="h-40 w-full" /> : order.isError ? <ErrorState error={order.error} onRetry={() => order.refetch()} /> : (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-lg ring-1 ring-line">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="bg-sunken/60 text-xs text-ink-600">
                <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-right">Ready</th><th className="px-3 py-2 text-right">Delivered</th>
                  <th className="w-28 px-3 py-2 text-right">Dispatch now</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {lines.map((i: any) => {
                  const ready = Number(i.reservedQty);
                  return (
                    <tr key={i.id}>
                      <td className="px-3 py-2"><p className="font-medium text-ink-900">{i.description}</p>
                        {i.modelNumber && <p className="text-xs text-ink-500">{i.modelNumber}</p>}</td>
                      <td className="tabular px-3 py-2 text-right">{qtyLabel(i.quantity)}</td>
                      <td className="tabular px-3 py-2 text-right">{qtyLabel(ready)}</td>
                      <td className="tabular px-3 py-2 text-right">{qtyLabel(i.deliveredQty)}</td>
                      <td className="px-3 py-2">
                        <Input type="number" min={0} max={ready} step="any" disabled={ready <= 0} className="text-right"
                          aria-label={`Dispatch quantity for ${i.description}`}
                          value={qty[i.id] ?? ''} onChange={(e) => setQty({ ...qty, [i.id]: e.target.value })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {fe('items') && <p className="text-xs text-danger">{fe('items')}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Transporter"><Input value={f.transporter} onChange={(e) => setF({ ...f, transporter: e.target.value })} /></Field>
            <Field label="Vehicle number"><Input value={f.vehicleNo} onChange={(e) => setF({ ...f, vehicleNo: e.target.value })} /></Field>
            <Field label="LR / docket number"><Input value={f.lrNumber} onChange={(e) => setF({ ...f, lrNumber: e.target.value })} /></Field>
            <Field label="Dispatch date" error={fe('dispatchDate')}>
              <Input type="date" value={f.dispatchDate} onChange={(e) => setF({ ...f, dispatchDate: e.target.value })} /></Field>
          </div>
          <Field label="Delivery address" hint="Defaults to the project's site address.">
            <Textarea rows={2} value={f.deliveryAddress} onChange={(e) => setF({ ...f, deliveryAddress: e.target.value })} /></Field>
        </div>
      )}
    </Modal>
  );
}
